// @spec GHG-STR-042
'use client';

/**
 * Verification checklist detail page - shows checklist items and allows completion tracking.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Loader2 } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  useLinkEvidence,
  useUnlinkEvidence,
  useUpdateChecklistItem,
  useVerificationChecklist,
} from '@/features/ghg/api/use-verification-checklist';
import { GenerateVerificationPackageDialog } from '@/features/ghg/components/GenerateVerificationPackageDialog';
import { VerificationChecklist } from '@/features/ghg/components/VerificationChecklist';
import { VerificationSummaryCard } from '@/features/ghg/components/VerificationSummaryCard';
import type { UpdateChecklistItemInput } from '@/features/ghg/types/verification-checklist';
import i18n from '@/lib/i18n';

export const Route = createFileRoute('/_authenticated/ghg/verification/$checklistId')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.verificationChecklist'),
  }),
  component: VerificationChecklistDetailPage,
});

/**
 * Verification checklist detail page.
 *
 * Displays:
 * - Summary card with progress and status
 * - Checklist items grouped by category
 * - Option to generate verification package
 */
function VerificationChecklistDetailPage() {
  const { t } = useTranslation('ghg');
  const navigate = useNavigate();
  const { checklistId } = Route.useParams();
  const { checklist, items, isLoading, isError } = useVerificationChecklist(checklistId);
  const [isPackageDialogOpen, setIsPackageDialogOpen] = React.useState(false);
  const [updatingItemId, setUpdatingItemId] = React.useState<string | null>(null);

  // Mutations
  const updateItemMutation = useUpdateChecklistItem(checklistId);
  const linkEvidenceMutation = useLinkEvidence(checklistId);
  const unlinkEvidenceMutation = useUnlinkEvidence(checklistId);

  const handleBack = () => {
    void navigate({ to: '/ghg/verification' });
  };

  const handleGeneratePackage = () => {
    setIsPackageDialogOpen(true);
  };

  const handleUpdateItem = React.useCallback(
    (itemId: string, data: UpdateChecklistItemInput) => {
      setUpdatingItemId(itemId);
      updateItemMutation.mutate(
        { itemId, data },
        {
          onSettled: () => {
            setUpdatingItemId(null);
          },
        }
      );
    },
    [updateItemMutation]
  );

  const handleLinkEvidence = React.useCallback(
    (itemId: string, evidenceId: string) => {
      linkEvidenceMutation.mutate({ itemId, data: { evidenceId } });
    },
    [linkEvidenceMutation]
  );

  const handleUnlinkEvidence = React.useCallback(
    (itemId: string, evidenceId: string) => {
      unlinkEvidenceMutation.mutate({ itemId, evidenceId });
    },
    [unlinkEvidenceMutation]
  );

  // Loading state
  if (isLoading) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-5xl">
          <Button variant="ghost" size="sm" className="-ml-2" onClick={handleBack}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('verification.back')}
          </Button>
          <div className="flex items-center justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (isError || !checklist) {
    return (
      <div className="p-8">
        <div className="mx-auto max-w-5xl space-y-6">
          <Button variant="ghost" size="sm" className="-ml-2" onClick={handleBack}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            {t('verification.back')}
          </Button>
          <Card className="border-destructive">
            <CardHeader>
              <CardTitle className="text-destructive">{t('verification.errorLoadingChecklist')}</CardTitle>
              <CardDescription>
                {t('verification.checklistNotFound')}
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="mx-auto max-w-5xl space-y-6">
        {/* Back button */}
        <Button variant="ghost" size="sm" className="-ml-2" onClick={handleBack}>
          <ArrowLeft className="mr-1 h-4 w-4" />
          {t('verification.backToChecklists')}
        </Button>

        {/* Header */}
        <header>
          <h1 className="text-3xl font-semibold text-foreground">
            {t('verification.yearVerificationChecklist', { year: checklist.inventoryYear })}
          </h1>
          <p className="mt-1 text-muted-foreground">
            {t('verification.trackReadiness', { year: checklist.inventoryYear })}
          </p>
        </header>

        {/* Summary Card */}
        <VerificationSummaryCard
          checklist={checklist}
          onGeneratePackage={handleGeneratePackage}
          isGenerating={false}
        />

        {/* Checklist Items */}
        <Card>
          <CardHeader>
            <CardTitle>{t('verification.checklistItems')}</CardTitle>
            <CardDescription>
              {t('verification.checklistItemsDesc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <VerificationChecklist
              checklistId={checklistId}
              items={items}
              assuranceLevel={checklist.assuranceLevel}
              onUpdateItem={handleUpdateItem}
              onLinkEvidence={handleLinkEvidence}
              onUnlinkEvidence={handleUnlinkEvidence}
              updatingItemId={updatingItemId}
            />
          </CardContent>
        </Card>

        {/* Generate Package Dialog */}
        <GenerateVerificationPackageDialog
          open={isPackageDialogOpen}
          onOpenChange={setIsPackageDialogOpen}
          checklistId={checklistId}
          inventoryYear={checklist.inventoryYear}
        />
      </div>
    </div>
  );
}
